/**
 * Billing Settings API
 *
 * Endpoints for billing management: settings retrieval, payment method
 * management, strategy updates (super admin), and pricing overrides (super admin).
 */

import { post, put, get, del } from 'aws-amplify/api';
import { processResponse } from './api_util';
import useSWR from 'swr';


// ──────────────────────────────────────────────────────────────────────
// GET billing settings
// ──────────────────────────────────────────────────────────────────────

async function getBillingSettings(customerID, caseID) {
    const queryParams = { customerID };
    if (caseID) {
        // Adds `casePaymentMethod` (the per-case payment method override) to the response.
        queryParams.caseID = caseID;
    }
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/settings',
        options: {
            queryParams,
        },
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export function useGetBillingSettings(customerID, shouldFetch = true, caseID = null) {
    return useSWR(
        shouldFetch && customerID ? ['BILLING_SETTINGS', customerID, caseID || null] : null,
        () => getBillingSettings(customerID, caseID)
    );
}

// ──────────────────────────────────────────────────────────────────────
// GET billing usage report
// ──────────────────────────────────────────────────────────────────────

async function getCaseUsageSummary(caseID) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/usage',
        options: {
            queryParams: { caseID },
        },
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export function useGetCaseUsageSummary(caseID, shouldFetch = true) {
    return useSWR(
        shouldFetch && caseID ? ['CASE_USAGE', caseID] : null,
        () => getCaseUsageSummary(caseID)
    );
}

// ──────────────────────────────────────────────────────────────────────
// GET customer effective price list (strategy + overrides, billable SKUs)
// ──────────────────────────────────────────────────────────────────────

async function getCustomerPriceList(customerID) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/price_list',
        options: {
            queryParams: { customerID },
        },
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export function useCustomerPriceList(customerID, shouldFetch = true) {
    return useSWR(
        shouldFetch && customerID ? ['CUSTOMER_PRICE_LIST', customerID] : null,
        () => getCustomerPriceList(customerID)
    );
}


// ──────────────────────────────────────────────────────────────────────
// Payment method management
// ──────────────────────────────────────────────────────────────────────

export async function storePaymentMethod({ customerID, paymentToken, tokenType = "CreditCard", recaptchaToken, billingAddress, setAsDefault = false }) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'customer_setup/payment',
        options: {
            body: {
                customerID,
                paymentToken,
                tokenType,
                recaptchaToken,
                billingAddress,
                setAsDefault,
            },
        },
    };
    const restOperation = post(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export async function deletePaymentMethod({ customerID, methodId, methodType }) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/payment_method/delete',
        options: {
            body: {
                customerID,
                methodId,
                methodType,
            },
        },
    };
    const restOperation = post(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export async function updatePaymentMethod({ customerID, oldMethodId, oldMethodType, paymentToken, tokenType = "CreditCard", recaptchaToken, billingAddress, setAsDefault = false }) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/payment_method/update',
        options: {
            body: {
                customerID,
                oldMethodId,
                oldMethodType,
                paymentToken,
                tokenType,
                recaptchaToken,
                billingAddress,
                setAsDefault,
            },
        },
    };
    const restOperation = post(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export async function setDefaultPaymentMethod({ customerID, methodId, methodType }) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/payment_method/default',
        options: {
            body: {
                customerID,
                methodId,
                methodType,
            },
        },
    };
    const restOperation = post(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

/**
 * Set or clear the per-case payment method override. Pass methodId/methodType
 * as null to clear (the case then charges the customer default).
 */
export async function setCasePaymentMethod({ customerID, caseID, methodId = null, methodType = null }) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/payment_method/case_default',
        options: {
            body: {
                customerID,
                caseID,
                methodId,
                methodType,
            },
        },
    };
    const restOperation = post(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

// ──────────────────────────────────────────────────────────────────────
// Billing strategy (super admin)
// ──────────────────────────────────────────────────────────────────────

export async function updateBillingStrategy({ customerID, billingStrategy }) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/strategy',
        options: {
            body: {
                customerID,
                billingStrategy,
            },
        },
    };
    const restOperation = put(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

async function getAvailableStrategies() {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/strategies',
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export function useGetAvailableStrategies(shouldFetch = true) {
    return useSWR(
        shouldFetch ? 'AVAILABLE_STRATEGIES' : null,
        () => getAvailableStrategies()
    );
}


// ──────────────────────────────────────────────────────────────────────
// Pricing overrides (super admin)
// ──────────────────────────────────────────────────────────────────────

export async function upsertBillingOverride({ customerID, sku, ...overrideData }) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/override',
        options: {
            body: {
                customerID,
                sku,
                ...overrideData,
            },
        },
    };
    const restOperation = post(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export async function deleteBillingOverride({ customerID, sku }) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'billing/override/delete',
        options: {
            body: {
                customerID,
                sku,
            },
        },
    };
    const restOperation = post(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export async function updateCustomerDetails(payload) {
    const restOperation = post({
        apiName: 'CustomerSetupApi',
        path: 'billing/customer_details',
        options: { body: payload },
    });
    const { body } = await restOperation.response;
    return await body.json();
}

// ──────────────────────────────────────────────────────────────────────
// Super Admin Customer Setup, manual mid-period charge, and invoice finalization endpoints
// ──────────────────────────────────────────────────────────────────────

export async function adminCreateOrgCustomer(payload) {
    const restOperation = post({
        apiName: 'CustomerSetupApi',
        path: 'admin/customer',
        options: { body: payload },
    });
    const { body } = await restOperation.response;
    return await body.json();
}

export async function adminMidperiodCharge(caseID) {
    const restOperation = post({
        apiName: 'CustomerSetupApi',
        path: 'admin/midperiod_charge',
        options: { body: { caseID } },
    });
    const { body } = await restOperation.response;
    return await body.json();
}

export async function adminFinalizeInvoice(caseID) {
    const restOperation = post({
        apiName: 'CustomerSetupApi',
        path: 'admin/finalize',
        options: { body: { caseID } },
    });
    const { body } = await restOperation.response;
    return await body.json();
}